<script lang="ts">
    import { readable } from "svelte/store";
    import LL from "../../../../i18n/i18n-svelte";
    import type {
        ChatPollAnswer,
        ChatPollItem,
        ChatRoomSidePanelHydrationState,
    } from "../../Connection/ChatConnection";
    import { IconCheck, IconLoader, IconMessageCircle2 } from "@wa-icons";

    interface Props {
        poll: ChatPollItem;
        onFocusPoll: (() => Promise<void>) | (() => void);
    }

    let { poll, onFocusPoll }: Props = $props();

    const readyHydrationState = readable<ChatRoomSidePanelHydrationState>({ status: "ready" });
    let { state: pollState, canVote } = $derived(poll);
    let localSelection: string[] = $state([]);
    let isDirty = $state(false);
    let isSubmittingVote = $state(false);
    let hasVoteError = $state(false);
    let isRetryingHydration = $state(false);

    let pollHydrationState = $derived(poll.hydrationState ?? readyHydrationState);
    let isFallbackPoll = $derived($pollHydrationState.status === "error" || $pollHydrationState.status === "loading");

    $effect(() => {
        if (!isDirty) {
            localSelection = [...$pollState.myAnswerIds];
        }
    });

    let winningAnswers = $derived($pollState.answers.filter((answer) => answer.isWinning));
    let sortedAnswers = $derived(
        [...$pollState.answers].sort((left, right) => {
            if (left.isWinning !== right.isWinning) {
                return left.isWinning ? -1 : 1;
            }

            return right.votes - left.votes;
        }),
    );

    function getAnswerResult(answer: ChatPollAnswer) {
        return `${answer.votes} (${answer.percentage}%)`;
    }

    function computeNextSelection(answerId: string): string[] {
        if ($pollState.maxSelections === 1) {
            return localSelection[0] === answerId ? [] : [answerId];
        }

        if (localSelection.includes(answerId)) {
            return localSelection.filter((existingAnswerId) => existingAnswerId !== answerId);
        }

        if (localSelection.length >= $pollState.maxSelections) {
            return [...localSelection.slice(1), answerId];
        }

        return [...localSelection, answerId];
    }

    function voteFromAnswerClick(answerId: string) {
        if (isFallbackPoll || $pollState.isEnded || !$canVote || isSubmittingVote) {
            return;
        }

        hasVoteError = false;
        const nextSelection = computeNextSelection(answerId);

        if (haveSameSelection(nextSelection, $pollState.myAnswerIds)) {
            return;
        }

        isSubmittingVote = true;
        isDirty = true;
        localSelection = [...nextSelection];

        poll.vote(nextSelection)
            .catch((error) => {
                console.error("Failed to submit poll vote", error);
                hasVoteError = true;
            })
            .finally(() => {
                isDirty = false;
                isSubmittingVote = false;
            });
    }

    function haveSameSelection(left: string[], right: string[]) {
        if (left.length !== right.length) {
            return false;
        }

        const leftSorted = [...left].sort();
        const rightSorted = [...right].sort();
        return leftSorted.every((answerId, index) => answerId === rightSorted[index]);
    }

    function retryPollHydration() {
        if (!poll.retryHydration || isRetryingHydration) {
            return;
        }

        isRetryingHydration = true;
        poll.retryHydration()
            .catch((error) => {
                console.error("Failed to retry poll hydration", error);
            })
            .finally(() => {
                isRetryingHydration = false;
            });
    }
</script>

<div class="min-w-0">
    <div class="flex items-center gap-2">
        <span
            class="rounded-full border border-solid px-2 py-0.5 text-xxs font-bold uppercase {$pollState.isEnded
                ? 'border-mdark/10 bg-mdark/5 text-mdark/60'
                : 'border-success/30 bg-success/15 text-success'}"
            data-testid="roomSidePanelPollStatus"
        >
            {$pollState.isEnded ? $LL.chat.poll.kind.closed() : $LL.chat.poll.kind.open()}
        </span>
        {#if isFallbackPoll}
            <span class="text-xs text-mdark/50">
                {$pollHydrationState.status === "loading" ? "" : $LL.chat.roomPanel.status.pollDetailsUnavailable()}
            </span>
        {:else}
            <span class="text-xs text-mdark/50">{$LL.chat.poll.participants({ count: $pollState.totalVotes })}</span>
        {/if}
    </div>

    <div class="mt-2 line-clamp-2 text-sm font-semibold text-mdark" data-testid="roomSidePanelPollQuestion">
        {$pollState.question}
    </div>

    {#if poll.context.kind === "thread"}
        <div class="mt-2 flex items-center gap-1.5 text-xs text-mdark/55" data-testid="roomSidePanelPollThreadContext">
            <IconMessageCircle2 font-size={13} />
            <span class="truncate">
                {#if poll.context.threadSenderName}{poll.context.threadSenderName}:
                {/if}{poll.context.threadPreview ?? $LL.chat.thread.defaultPreview()}
            </span>
        </div>
    {/if}

    {#if isFallbackPoll}
        <div class="mt-3 rounded-lg border border-solid border-mdark/10 bg-mdark/5 px-2.5 py-2 text-xs text-mdark/60">
            {#if $pollHydrationState.status === "loading"}
                <div class="flex items-center gap-2">
                    <IconLoader class="animate-[spin_2s_linear_infinite]" font-size={14} />
                </div>
            {:else}
                {$LL.chat.roomPanel.status.pollDetailsUnavailable()}
            {/if}
        </div>
    {:else if !$pollState.isEnded && $canVote}
        <div class="mt-3 flex flex-col gap-1.5">
            {#each $pollState.answers as answer (answer.id)}
                {@const selected = localSelection.includes(answer.id)}
                <button
                    type="button"
                    data-testid={`roomSidePanelPollAnswer-${answer.id}`}
                    aria-pressed={selected}
                    class="m-0 flex min-w-0 items-center justify-between gap-2 rounded-lg border border-solid px-2.5 py-2 text-left text-xs transition-colors {selected
                        ? 'border-mblue/40 bg-mblue/10 text-mdark'
                        : 'border-mdark/10 bg-mdark/5 text-mdark/80 hover:bg-mdark/10'}"
                    disabled={isSubmittingVote}
                    onclick={() => voteFromAnswerClick(answer.id)}
                >
                    <span class="flex min-w-0 items-center gap-2">
                        <span
                            class="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-solid {selected
                                ? 'border-mblue bg-mblue/20 text-mblue'
                                : 'border-mdark/20'}"
                        >
                            {#if selected}
                                <IconCheck font-size={12} />
                            {/if}
                        </span>
                        <span class="truncate">{answer.text}</span>
                    </span>
                    {#if isSubmittingVote && selected}
                        <IconLoader class="shrink-0 animate-[spin_2s_linear_infinite]" font-size={13} />
                    {:else if $pollState.resultsVisible}
                        <span class="shrink-0 text-mdark/60">{getAnswerResult(answer)}</span>
                    {/if}
                </button>
            {/each}
        </div>
    {:else if $pollState.resultsVisible}
        <div class="mt-3 flex flex-col gap-1.5">
            {#each sortedAnswers.slice(0, 3) as answer (answer.id)}
                <div class="text-xs text-mdark/70">
                    <div class="mb-1 flex items-center justify-between gap-2">
                        <span class="truncate {answer.isWinning ? 'font-semibold text-mdark' : ''}">{answer.text}</span>
                        <span class="shrink-0">{getAnswerResult(answer)}</span>
                    </div>
                    <div class="h-1.5 overflow-hidden rounded-full bg-mdark/10">
                        <div
                            class="h-full rounded-full {answer.isWinning ? 'bg-mblue' : 'bg-mdark/25'}"
                            style:width={`${answer.percentage}%`}
                        ></div>
                    </div>
                </div>
            {/each}
        </div>
    {/if}

    {#if !isFallbackPoll && !$pollState.resultsVisible && !$pollState.isEnded}
        <div class="mt-3 text-xs text-mdark/50">
            {$pollState.kind === "open" ? $LL.chat.poll.resultsAfterVote() : $LL.chat.poll.resultsWhenClosed()}
        </div>
    {/if}

    {#if $pollState.isEnded && !isFallbackPoll && winningAnswers.length > 0}
        <div class="mt-3 text-xs font-semibold text-mdark/80">
            {$LL.chat.roomPanel.pollWinner({ answer: winningAnswers.map((answer) => answer.text).join(", ") })}
        </div>
    {/if}

    {#if hasVoteError}
        <div class="mt-3 rounded-lg bg-mdanger/10 px-2.5 py-2 text-xs text-mdanger">
            {$LL.chat.poll.submitError()}
        </div>
    {/if}

    <div class="mt-3 flex flex-wrap gap-2">
        {#if $pollHydrationState.status === "error" && poll.retryHydration}
            <button
                type="button"
                class="m-0 rounded-lg border border-solid border-mdark/10 bg-mdark/5 px-3 py-2 text-xs font-semibold text-mdark/75 transition-colors hover:bg-mdark/10 hover:text-mdark"
                data-testid="roomSidePanelPollRetryButton"
                disabled={isRetryingHydration}
                onclick={retryPollHydration}
            >
                {#if isRetryingHydration}
                    <IconLoader class="animate-[spin_2s_linear_infinite]" font-size={14} />
                {:else}
                    {$LL.chat.roomPanel.status.retry()}
                {/if}
            </button>
        {/if}

        <button
            type="button"
            class="m-0 rounded-lg border border-solid border-mdark/10 bg-mdark/5 px-3 py-2 text-xs font-semibold text-mdark/75 transition-colors hover:bg-mdark/10 hover:text-mdark"
            data-testid="roomSidePanelPollFocusButton"
            onclick={() => onFocusPoll()}
        >
            {$LL.chat.show()}
        </button>
    </div>
</div>
